import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { FileDown, X, FolderOpen } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useKeyboard } from './hooks/useKeyboard';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider, useToast } from './context/ToastContext';
import { TransactionProvider } from './context/TransactionContext';
import { ConfigProvider } from './context/ConfigContext';
import { PWAProvider } from './context/PWAContext';
import { NetworkProvider } from './context/NetworkContext';
import { PasscodeProvider, usePasscode } from './context/PasscodeContext';
import { OnboardingProvider } from './context/OnboardingContext';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import OnboardingOverlay from './components/common/OnboardingOverlay';
import { setAccessoryBarVisible, initNative } from './services/native';


// Native splash plugin
const Splash = registerPlugin('Splash');
const hideSplash = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      await Splash.hide();
    } catch (e) {
      // Ignore errors
    }
  }
};

// Request native permissions on app start (mobile only)
// Only notification permission is requested at startup (needed for scheduled/push notifications).
// Camera and microphone permissions are requested on-demand when the user triggers those features,
// per Apple's guidelines (avoid requesting permissions before the user needs them).
const requestNativePermissions = async () => {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const PERMISSION_VERSION = 'v3';
    const hasAsked = localStorage.getItem('native_permissions_version');
    if (hasAsked === PERMISSION_VERSION) return;

    // Request notification permission
    try {
      const notifResult = await LocalNotifications.requestPermissions();
      console.log('[App] Notification permission result:', notifResult.display);
    } catch (e) {
      console.error('[App] Error requesting notification permission:', e);
    }

    localStorage.setItem('native_permissions_version', PERMISSION_VERSION);
  } catch (e) {
    console.error('[App] Error requesting native permissions:', e);
  }
};

// QueryClient with persistent localStorage caching
// Cached data displays instantly; background refetch after 30s keeps it in sync
// with changes from the mobile app or other clients.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,           // 30s — show cached, refetch in background if older
      gcTime: Infinity,            // Never garbage collect — keep data for instant display
      retry: 1,
      refetchOnWindowFocus: true,  // Refetch when user switches back to the tab
      refetchOnMount: true,        // Refetch on mount if stale (> 30s)
      refetchOnReconnect: true,    // Refetch when network comes back
    }
  }
});

// Persist all query cache to localStorage — survives app restart & offline
const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'hj-query-cache',
});
import Layout from './components/layout/Layout';
import DashboardWrapper from './components/dashboard/DashboardWrapper';
import TransactionList from './components/transactions/TransactionList';
import CalendarPage from './components/calendar/CalendarPage';
import TaxCalculator from './components/tax/TaxCalculator';
import CategoryPage from './components/category/CategoryPage';
import AccountPage from './components/account/AccountPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SetPasswordPage from './pages/SetPasswordPage';
import SettingsPage from './pages/SettingsPage';
import StatsPage from './pages/StatsPage';
import ConfigurationPage from './pages/ConfigurationPage';
import SecuritySetupPage from './pages/SecuritySetupPage';
import AdminPage from './pages/AdminPage';
import RecurringTransactionsPage from './pages/RecurringTransactionsPage';
import InsightsPage from './pages/InsightsPage';
import PasscodeSetupPage from './pages/PasscodeSetupPage';
import LedgerPage from './pages/LedgerPage';
import TrendPage from './pages/TrendPage';
import ReceivablesPage from './pages/ReceivablesPage';
import DebtsPage from './pages/DebtsPage';
import ContactsPage from './pages/ContactsPage';
import PricingPage from './pages/PricingPage';
import SubscriptionPage from './pages/SubscriptionPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
// Form pages for mobile-friendly full-page forms
import TransactionFormPage from './pages/TransactionFormPage';
import ScanPayPage from './pages/ScanPayPage';
import ScanPayFormPage from './pages/ScanPayFormPage';
import AccountFormPage from './pages/AccountFormPage';
import CategoryFormPage from './pages/CategoryFormPage';
import RecurringFormPage from './pages/RecurringFormPage';
import DebtFormPage from './pages/DebtFormPage';
import ReceivableFormPage from './pages/ReceivableFormPage';
import ContactFormPage from './pages/ContactFormPage';
import DebtDetailPage from './pages/DebtDetailPage';
import ReceivableDetailPage from './pages/ReceivableDetailPage';
import RecordPaymentPage from './pages/RecordPaymentPage';
import TransactionDetailPage from './pages/TransactionDetailPage';
import BudgetCategoryPage from './pages/BudgetCategoryPage';
import CategoryTransactionsPage from './pages/CategoryTransactionsPage';
import NotificationsPage from './pages/NotificationsPage';
import ReferralPage from './pages/ReferralPage';
import ChecklistPage from './pages/ChecklistPage';
import NotFoundPage from './pages/NotFoundPage';
import LoadingBar from './components/common/LoadingBar';
import PasscodeLockScreen from './components/common/PasscodeLockScreen';
import PWAPrompt from './components/common/PWAPrompt';
import ToastContainer from './components/common/ToastContainer';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, hasEncryptionKey } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to security setup if encryption key not set
  if (!hasEncryptionKey) {
    return <Navigate to="/security-setup" replace />;
  }

  return children;
}

// Subscription Required Route - blocks expired users (bypassed in free access mode or for admins)
function SubscriptionRequiredRoute({ children }) {
  const { isExpired, isCancelled, isPaused, subscriptionDaysLeft, freeAccessMode, loading: subscriptionLoading } = useSubscription();
  const { user } = useAuth();

  // Block expired users and cancelled/paused users whose period has ended
  const isCancelledAndExpired = isCancelled && subscriptionDaysLeft <= 0;
  const isPausedAndExpired = isPaused && subscriptionDaysLeft <= 0;
  if (!subscriptionLoading && (isExpired || isCancelledAndExpired || isPausedAndExpired) && !freeAccessMode && !user?.isAdmin) {
    return <Navigate to="/pricing" replace />;
  }

  return children;
}

// Password Setup Route - for social auth users who need to set password
function PasswordSetupRoute({ children }) {
  const { isAuthenticated, needsPasswordSetup } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If password is already set, redirect to next step or dashboard
  if (!needsPasswordSetup) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Security Setup Route - for users who need to set up encryption key
function SecuritySetupRoute({ children }) {
  const { isAuthenticated, hasEncryptionKey } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If already has key, redirect to dashboard
  if (hasEncryptionKey) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Public Route wrapper (redirects to dashboard if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  // Show children (login page) while loading - no spinner
  if (loading) {
    return children;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Admin Route wrapper - only for admin users
function AdminRoute({ children }) {
  const { user, isAuthenticated } = useAuth();

  // If not authenticated (logout), let ProtectedRoute handle redirect to /login
  if (!isAuthenticated) return null;

  if (!user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Redirect admin users to admin panel from home page
function AdminRedirect({ children }) {
  const { user } = useAuth();

  if (user?.isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return children;
}

// Passcode Gate - shows lock screen when app is locked
function PasscodeGate({ children }) {
  const { isAuthenticated } = useAuth();
  const { isLocked, isDesktopLocked, isPasscodeEnabled, isPasscodeSet } = usePasscode();

  // Only show lock screen if user is authenticated and passcode is enabled/set
  if (isAuthenticated && isPasscodeEnabled && isPasscodeSet && (isLocked || isDesktopLocked)) {
    return <PasscodeLockScreen />;
  }

  return children;
}

// App content with routes
function AppContent() {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const { hidePaymentUI } = useSubscription();

  // Global keyboard handler — scrolls focused input into view on iOS when keyboard opens
  useKeyboard();

  // Handle pending notification navigation (from push notification tap)
  useEffect(() => {
    if (!loading && isAuthenticated) {
      const pendingNav = sessionStorage.getItem('pendingNotificationNav');
      if (pendingNav) {
        sessionStorage.removeItem('pendingNotificationNav');
        navigate(pendingNav, { replace: true });
      }
    }
  }, [loading, isAuthenticated, navigate]);

  // Handle subscription expired — cancelled goes to /subscription (resubscribe), others to /pricing
  useEffect(() => {
    const subHandler = (e) => {
      const code = e.detail?.code;
      const target = code === 'SUBSCRIPTION_ENDED' ? '/subscription' : '/pricing';
      navigate(target, { replace: true });
    };
    window.addEventListener('subscription-expired', subHandler);
    return () => window.removeEventListener('subscription-expired', subHandler);
  }, [navigate]);

  // Handle auth expired (401 token expired) — redirect to login (safe, no page reload)
  useEffect(() => {
    const authHandler = () => navigate('/login', { replace: true });
    window.addEventListener('auth-expired', authHandler);
    return () => window.removeEventListener('auth-expired', authHandler);
  }, [navigate]);

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Password Setup - for social auth users */}
      <Route
        path="/set-password"
        element={
          <PasswordSetupRoute>
            <SetPasswordPage />
          </PasswordSetupRoute>
        }
      />

      {/* Security Setup - required before accessing app */}
      <Route
        path="/security-setup"
        element={
          <SecuritySetupRoute>
            <SecuritySetupPage />
          </SecuritySetupRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Routes that require active subscription */}
        <Route index element={<AdminRedirect><SubscriptionRequiredRoute><DashboardWrapper /></SubscriptionRequiredRoute></AdminRedirect>} />
        <Route path="transactions" element={<SubscriptionRequiredRoute><TransactionList /></SubscriptionRequiredRoute>} />
        <Route path="transactions/new" element={<SubscriptionRequiredRoute><TransactionFormPage /></SubscriptionRequiredRoute>} />
        <Route path="scan-pay" element={<SubscriptionRequiredRoute><ScanPayPage /></SubscriptionRequiredRoute>} />
        <Route path="scan-pay/pay" element={<SubscriptionRequiredRoute><ScanPayFormPage /></SubscriptionRequiredRoute>} />
        <Route path="transactions/:id" element={<SubscriptionRequiredRoute><TransactionDetailPage /></SubscriptionRequiredRoute>} />
        <Route path="transactions/:id/edit" element={<SubscriptionRequiredRoute><TransactionFormPage /></SubscriptionRequiredRoute>} />
        <Route path="budget-category" element={<SubscriptionRequiredRoute><BudgetCategoryPage /></SubscriptionRequiredRoute>} />
        <Route path="category-transactions" element={<SubscriptionRequiredRoute><CategoryTransactionsPage /></SubscriptionRequiredRoute>} />
        <Route path="recurring" element={<SubscriptionRequiredRoute><RecurringTransactionsPage /></SubscriptionRequiredRoute>} />
        <Route path="recurring/new" element={<SubscriptionRequiredRoute><RecurringFormPage /></SubscriptionRequiredRoute>} />
        <Route path="recurring/:id/edit" element={<SubscriptionRequiredRoute><RecurringFormPage /></SubscriptionRequiredRoute>} />
        <Route path="checklist" element={<SubscriptionRequiredRoute><ChecklistPage /></SubscriptionRequiredRoute>} />
        <Route path="receivables" element={<SubscriptionRequiredRoute><ReceivablesPage /></SubscriptionRequiredRoute>} />
        <Route path="receivables/new" element={<SubscriptionRequiredRoute><ReceivableFormPage /></SubscriptionRequiredRoute>} />
        <Route path="receivables/:id" element={<SubscriptionRequiredRoute><ReceivableDetailPage /></SubscriptionRequiredRoute>} />
        <Route path="receivables/:id/edit" element={<SubscriptionRequiredRoute><ReceivableFormPage /></SubscriptionRequiredRoute>} />
        <Route path="receivables/:id/payment" element={<SubscriptionRequiredRoute><RecordPaymentPage /></SubscriptionRequiredRoute>} />
        <Route path="debts" element={<SubscriptionRequiredRoute><DebtsPage /></SubscriptionRequiredRoute>} />
        <Route path="debts/new" element={<SubscriptionRequiredRoute><DebtFormPage /></SubscriptionRequiredRoute>} />
        <Route path="debts/:id" element={<SubscriptionRequiredRoute><DebtDetailPage /></SubscriptionRequiredRoute>} />
        <Route path="debts/:id/edit" element={<SubscriptionRequiredRoute><DebtFormPage /></SubscriptionRequiredRoute>} />
        <Route path="debts/:id/payment" element={<SubscriptionRequiredRoute><RecordPaymentPage /></SubscriptionRequiredRoute>} />
        <Route path="contacts" element={<SubscriptionRequiredRoute><ContactsPage /></SubscriptionRequiredRoute>} />
        <Route path="contacts/new" element={<SubscriptionRequiredRoute><ContactFormPage /></SubscriptionRequiredRoute>} />
        <Route path="contacts/:id/edit" element={<SubscriptionRequiredRoute><ContactFormPage /></SubscriptionRequiredRoute>} />
        <Route path="calendar" element={<SubscriptionRequiredRoute><CalendarPage /></SubscriptionRequiredRoute>} />
        <Route path="categories" element={<SubscriptionRequiredRoute><CategoryPage /></SubscriptionRequiredRoute>} />
        <Route path="categories/new" element={<SubscriptionRequiredRoute><CategoryFormPage /></SubscriptionRequiredRoute>} />
        <Route path="categories/:id/edit" element={<SubscriptionRequiredRoute><CategoryFormPage /></SubscriptionRequiredRoute>} />
        <Route path="accounts" element={<SubscriptionRequiredRoute><AccountPage /></SubscriptionRequiredRoute>} />
        <Route path="accounts/new" element={<SubscriptionRequiredRoute><AccountFormPage /></SubscriptionRequiredRoute>} />
        <Route path="accounts/:id/edit" element={<SubscriptionRequiredRoute><AccountFormPage /></SubscriptionRequiredRoute>} />
        <Route path="tax" element={<AdminRoute><TaxCalculator /></AdminRoute>} />
        <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="stats" element={<SubscriptionRequiredRoute><StatsPage /></SubscriptionRequiredRoute>} />
        <Route path="trend" element={<SubscriptionRequiredRoute><TrendPage /></SubscriptionRequiredRoute>} />
        <Route path="ledger" element={<SubscriptionRequiredRoute><LedgerPage /></SubscriptionRequiredRoute>} />
        <Route path="insights" element={<SubscriptionRequiredRoute><InsightsPage /></SubscriptionRequiredRoute>} />
        <Route path="configuration" element={<SubscriptionRequiredRoute><ConfigurationPage /></SubscriptionRequiredRoute>} />
        {/* Routes accessible even with expired subscription */}
        <Route path="settings" element={<Navigate to="/settings/profile" replace />} />
        <Route path="settings/:section" element={<SettingsPage />} />
        <Route path="passcode" element={<PasscodeSetupPage />} />
        {!hidePaymentUI && (
          <>
            <Route path="pricing" element={<PricingPage />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="checkout/success" element={<CheckoutSuccessPage />} />
          </>
        )}
        {!hidePaymentUI && (
          <Route path="referrals" element={<ReferralPage />} />
        )}
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="notifications/:id" element={<NotificationsPage />} />
      </Route>

      {/* Catch all - 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

// Download Complete Modal Component
function DownloadCompleteModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-100">
          <h3 className="text-lg font-semibold text-surface-900">Download Complete</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-surface-100 transition-colors"
          >
            <X className="w-5 h-5 text-surface-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <FileDown className="w-8 h-8 text-green-600" />
          </div>
          <h4 className="text-lg font-medium text-surface-900 mb-2">
            Your file is ready!
          </h4>
          <p className="text-sm text-surface-600 mb-4">
            The file has been saved to your device's <strong>Documents</strong> folder.
          </p>
          <div className="flex items-center justify-center gap-2 text-xs text-surface-500 bg-surface-50 rounded-lg p-3">
            <FolderOpen className="w-4 h-4" />
            <span>Files App → Documents</span>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-surface-100">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl text-white font-medium transition-colors"
            style={{ backgroundColor: 'var(--accent-bg, #3b82f6)' }}
          >
            OK, Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Hide native splash, initialize keyboard settings, and request notification permission when React app is ready
  useEffect(() => {
    hideSplash();

    // Initialize native features (status bar, back button, etc.)
    initNative();

    // iOS safe area: env(safe-area-inset-top) returns empty with contentInset:'never',
    // so we set a CSS variable based on screen height as fallback
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      const h = screen.height;
      // Dynamic Island (iPhone 14 Pro/15/16): 59px, Notch (iPhone X-14): 47px, Non-notch: 20px
      const safeTop = h >= 852 ? 59 : h >= 812 ? 47 : 20;
      const safeBottom = h >= 812 ? 34 : 0;
      document.documentElement.style.setProperty('--safe-area-top', safeTop + 'px');
      document.documentElement.style.setProperty('--safe-area-bottom', safeBottom + 'px');
    }

    // Initialize iOS keyboard settings
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      setAccessoryBarVisible(true);

      // Set keyboard resize mode at runtime (matches capacitor.config.ts)
      import('@capacitor/keyboard').then(({ Keyboard, KeyboardResize }) => {
        Keyboard.setResizeMode({ mode: KeyboardResize.Body });
      }).catch(() => {});

      // Initialize Apple IAP via RevenueCat
      import('./services/appleIAP').then(({ initAppleIAP }) => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        initAppleIAP(user?.id).catch(err => console.error('Apple IAP init error:', err));
      });
    }

    // Request native permissions after a short delay (better UX)
    const timer = setTimeout(() => {
      requestNativePermissions();
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Listen for notification clicks (mobile only)
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Add listener for notification action (click)
      const setupListener = async () => {
        await LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
          console.log('[App] Notification clicked:', notification);
          // Show the download complete modal
          setShowDownloadModal(true);
        });
      };
      setupListener();

      return () => {
        LocalNotifications.removeAllListeners();
      };
    }
  }, []);

  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister: localStoragePersister, maxAge: Infinity }}>
      <BrowserRouter>
        <ThemeProvider>
          <ConfigProvider>
            <ToastProvider>
              <AuthProvider>
                <SubscriptionProvider>
                  <PWAProvider>
                    <NetworkProvider>
                      <TransactionProvider>
                        <PasscodeProvider>
                          <OnboardingProvider>
                            <PasscodeGate>
                              <LoadingBar />
                              <AppContent />
                              <PWAPrompt />
                              <ToastContainer />
                              <DownloadCompleteModal
                                isOpen={showDownloadModal}
                                onClose={() => setShowDownloadModal(false)}
                              />
                              <OnboardingOverlay />
                            </PasscodeGate>
                          </OnboardingProvider>
                        </PasscodeProvider>
                      </TransactionProvider>
                    </NetworkProvider>
                  </PWAProvider>
                </SubscriptionProvider>
              </AuthProvider>
            </ToastProvider>
          </ConfigProvider>
        </ThemeProvider>
      </BrowserRouter>
      <Analytics />
      <SpeedInsights />
    </PersistQueryClientProvider>
  );
}
